/*
 * Payment amount rules (QA batch 17, PAY-013 / PAY-014; the server checks
 * the same in `PaymentAmounts`): a payment must be more than $0, no invoice
 * line may be negative, and paying more than an invoice's balance needs a
 * "Record it anyway?" confirmation (the server then gets allowOverpayment).
 */

export type PaymentLine = {
  amountPaid: string | number | null | undefined;
  balance: number;
  invoiceNo?: number | string | null;
  /**
   * Editing a saved payment (PAY-018): what this payment already paid on the
   * invoice. A line that isn't raised above it isn't asked about again (an
   * overpayment confirmed earlier stays saveable, like on the server).
   */
  previousAmount?: number;
};

const CENT = 0.005;

export const lineAmount = (value: PaymentLine["amountPaid"]): number => {
  const parsed = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const money = (value: number) =>
  value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Why the payment can't be saved, or null. */
export const paymentAmountProblem = (lines: PaymentLine[]): string | null => {
  if (lines.some((line) => lineAmount(line.amountPaid) < 0)) {
    return "Amounts can't be negative.";
  }
  const total = lines.reduce(
    (sum, line) => sum + lineAmount(line.amountPaid),
    0,
  );
  if (total < CENT) return "Enter an amount more than $0.";
  return null;
};

/** "This is $50.00 more than the balance…" for lines paying more than owed, else null. */
export const overpaymentWarning = (lines: PaymentLine[]): string | null => {
  const over = lines
    .map((line) => ({
      line,
      extra: lineAmount(line.amountPaid) - (line.balance || 0),
    }))
    .filter(
      (item) =>
        item.extra > CENT &&
        lineAmount(item.line.amountPaid) >
          (item.line.previousAmount || 0) + CENT,
    );
  if (!over.length) return null;
  if (over.length === 1) {
    const { line, extra } = over[0];
    const which = line.invoiceNo ? ` of invoice #${line.invoiceNo}` : "";
    return `This is ${money(extra)} more than the balance${which} (${money(
      line.balance || 0,
    )}). The invoice will show a negative balance. Record it anyway?`;
  }
  const extra = over.reduce((sum, item) => sum + item.extra, 0);
  return `This is ${money(extra)} more than the balances of ${
    over.length
  } invoices. They will show a negative balance. Record it anyway?`;
};
